import type { NextApiRequest, NextApiResponse } from 'next';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import { executeSyncForCompany } from '../../../lib/syncEngine';

/**
 * S3.5: Scheduled Sync Webhook
 * POST /api/webhooks/sync-cron
 * 
 * Called by n8n, pg_cron, or any external scheduler.
 * Authenticates via a shared secret (SYNC_WEBHOOK_SECRET).
 * Triggers sync for all active company mappings.
 */
export default async function handler(req: NextApiRequest, res: NextApiResponse) {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  // Authenticate webhook via shared secret
  const authHeader = req.headers['x-webhook-secret'] || req.headers['authorization'];
  const expectedSecret = process.env.SYNC_WEBHOOK_SECRET;

  if (!expectedSecret) {
    console.error('[Sync Cron] SYNC_WEBHOOK_SECRET not configured');
    return res.status(500).json({ error: 'Webhook not configured.' });
  }

  const token = typeof authHeader === 'string'
    ? authHeader.replace('Bearer ', '')
    : '';

  if (token !== expectedSecret) {
    return res.status(401).json({ error: 'Invalid webhook secret.' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();

    // Get all active company mappings with sync enabled
    const { data: mappings, error: mapError } = await supabaseAdmin
      .from('cegid_company_mapping')
      .select('id, cliente_id, cegid_company_code, api_type, tipo_opcion_fiscal')
      .eq('active', true)
      .eq('sync_enabled', true);

    if (mapError) throw mapError;

    if (!mappings || mappings.length === 0) {
      return res.status(200).json({
        success: true,
        message: 'No active company mappings found.',
        syncs_triggered: 0,
      });
    }

    const results: any[] = [];

    for (const mapping of mappings) {
      // Create a sync run record
      const { data: syncRun, error: runError } = await supabaseAdmin
        .from('cegid_sync_runs')
        .insert({
          sync_type: `cron_${mapping.api_type}`,
          status: 'running',
          company_mapping_id: mapping.id,
          metadata: {
            triggered_by: 'webhook_cron',
            company_code: mapping.cegid_company_code,
            api_type: mapping.api_type,
          },
        })
        .select()
        .single();

      if (runError) {
        results.push({
          mapping_id: mapping.id,
          company_code: mapping.cegid_company_code,
          status: 'error',
          error: runError.message,
        });
        continue;
      }

      // Execute sync directly (no HTTP self-fetch)
      try {
        const syncResult = await executeSyncForCompany(
          supabaseAdmin,
          mapping.cegid_company_code,
          syncRun.id,
          {
            tipoOpcionFiscal: mapping.tipo_opcion_fiscal ?? 1,
          }
        );

        results.push({
          mapping_id: mapping.id,
          company_code: mapping.cegid_company_code,
          sync_run_id: syncRun.id,
          status: syncResult.status === 'completed' ? 'completed' : 'failed',
          details: syncResult,
        });
      } catch (syncError: any) {
        // Mark sync run as failed
        await supabaseAdmin
          .from('cegid_sync_runs')
          .update({
            status: 'failed',
            error_message: syncError.message,
            completed_at: new Date().toISOString(),
          })
          .eq('id', syncRun.id);

        results.push({
          mapping_id: mapping.id,
          company_code: mapping.cegid_company_code,
          sync_run_id: syncRun.id,
          status: 'error',
          error: syncError.message,
        });
      }
    }

    // Update automation_schedule last_run
    await supabaseAdmin
      .from('automation_schedule')
      .upsert({
        task_name: 'cegid_sync_cron',
        description: 'Sincronización automática con Cegid',
        schedule_cron: '0 2 * * *',
        last_run_at: new Date().toISOString(),
        enabled: true,
      }, { onConflict: 'task_name' });

    return res.status(200).json({
      success: true,
      syncs_triggered: results.length,
      results,
      timestamp: new Date().toISOString(),
    });
  } catch (error: any) {
    console.error('[Sync Cron] Error:', error);
    return res.status(500).json({ error: 'Error en sincronización programada.' });
  }
}
